import { Filter, X } from "lucide-react";

const REMOTE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "Onsite" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "adzuna", label: "Adzuna" },
  { value: "seek", label: "Seek" },
  { value: "indeed", label: "Indeed" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "government_jobs", label: "Government" },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "immediate_income", label: "Immediate income" },
  { value: "transitional", label: "Transitional" },
  { value: "long_term_future_proof", label: "Long-term future-proof" },
];

const INPUT =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500";

export default function JobFilters({ filters, onChange, onClear, resultsCount }) {
  const set = (patch) => onChange({ ...filters, ...patch });

  const active =
    !!filters.location ||
    !!filters.salary_min ||
    !!filters.remote_type ||
    !!filters.role_category ||
    !!filters.source;

  return (
    <aside
      className="rounded-2xl border border-slate-200 bg-white p-5 lg:p-6 shadow-[0_4px_18px_rgb(0,0,0,0.03)]"
      data-testid="jobs-filters"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-medium text-slate-900">Filters</h3>
        </div>
        {active ? (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid="jobs-filters-clear"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        ) : null}
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="filter-location"
            className="block text-xs font-medium text-slate-600 mb-1.5"
          >
            Location
          </label>
          <input
            id="filter-location"
            type="text"
            value={filters.location || ""}
            onChange={(e) => set({ location: e.target.value || "" })}
            placeholder="e.g. Sydney, Remote, AU"
            className={INPUT}
            data-testid="filter-location"
          />
        </div>

        <div>
          <label
            htmlFor="filter-salary"
            className="block text-xs font-medium text-slate-600 mb-1.5"
          >
            Minimum salary
          </label>
          <input
            id="filter-salary"
            type="number"
            min={0}
            step={1000}
            value={filters.salary_min ?? ""}
            onChange={(e) =>
              set({
                salary_min:
                  e.target.value === "" ? "" : Number(e.target.value) || 0,
              })
            }
            placeholder="e.g. 60000"
            className={INPUT}
            data-testid="filter-salary-min"
          />
        </div>

        <div>
          <label
            htmlFor="filter-remote"
            className="block text-xs font-medium text-slate-600 mb-1.5"
          >
            Remote / Hybrid
          </label>
          <select
            id="filter-remote"
            value={filters.remote_type || ""}
            onChange={(e) => set({ remote_type: e.target.value })}
            className={INPUT}
            data-testid="filter-remote-type"
          >
            {REMOTE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="filter-category"
            className="block text-xs font-medium text-slate-600 mb-1.5"
          >
            Role category
          </label>
          <select
            id="filter-category"
            value={filters.role_category || ""}
            onChange={(e) => set({ role_category: e.target.value })}
            className={INPUT}
            data-testid="filter-role-category"
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="filter-source"
            className="block text-xs font-medium text-slate-600 mb-1.5"
          >
            Source
          </label>
          <select
            id="filter-source"
            value={filters.source || ""}
            onChange={(e) => set({ source: e.target.value })}
            className={INPUT}
            data-testid="filter-source"
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p
        className="mt-5 text-xs text-slate-500"
        data-testid="jobs-filters-count"
      >
        {`Showing ${resultsCount} ${resultsCount === 1 ? "match" : "matches"}`}
      </p>
    </aside>
  );
}
